import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  InspectionReviewService,
  MainInformationDto,
  ReviewSide,
  ReviewedDetectionDto,
} from "../../services/ocr/InspectionReviewService";
import { approveAll as markAllApproved } from "./analysisDetections";
import { stableKeyFor } from "./detectionAssessment";
import { snackbarNotificationsService } from "../../services/notifications/SnackbarNotificationsService";

interface UseApprovedListArgs {
  /** Which of the file's two approved lists this is. */
  side: ReviewSide;
  review: InspectionReviewService;
  /**
   * The machine output for this side (ocr.json rows, or the analysis output), already in
   * review-DTO shape. This is what an empty approved list is seeded from.
   */
  machineRows: ReviewedDetectionDto[];
  /** Main information travels with every save; it belongs to the review, not to one side. */
  mainInformation: MainInformationDto[];
}

export interface ApprovedList {
  /**
   * The rows to SHOW: the approved ones only. Never the machine output — a file with nothing
   * approved yet shows an empty graph and an empty detection list, which is the point.
   */
  rows: ReviewedDetectionDto[];
  /** Everything held for this side, approved or not — what a Save would persist. */
  allRows: ReviewedDetectionDto[];
  /** Rows held back from `rows` because their status is not Approved. */
  notApprovedCount: number;
  /** Whether anything is approved (draft included), which is what Compare needs. */
  hasApproved: boolean;
  /** Unsaved reviewer changes are pending. */
  dirty: boolean;
  /**
   * Whether pressing Save would achieve anything.
   *
   * Not the same as `dirty`. A list can be fully stored and still unapproved — which is the
   * state a refused approval leaves behind — and in that state the documents do not exist yet,
   * so Save has work to do even with nothing pending.
   */
  canSubmit: boolean;
  saving: boolean;
  /**
   * Insert or replace one row. `previous` must be given when editing an existing row, because
   * an edit can change the distance or main code — and therefore the row's identity.
   * Local only: nothing reaches the server until `save()`.
   */
  upsert(row: ReviewedDetectionDto, previous?: ReviewedDetectionDto): void;
  remove(row: ReviewedDetectionDto): void;
  /** Replace the whole list in one go (bulk edits, re-sync from the machine output). */
  replace(rows: ReviewedDetectionDto[]): void;
  /**
   * Copy every machine detection in (keeping the edits already made to rows that are present)
   * and mark the whole list Approved. This is how an empty list is populated.
   */
  approveAll(): void;
  /**
   * Throw the reviewer's work away and start again from the machine output, every row approved.
   *
   * Distinct from approveAll, which UNIONS: that keeps corrections already made and only fills in
   * what is missing, so it cannot undo a bad edit. This replaces, which is the only way back to
   * "exactly what OCR read" once the list has been worked on. Local until save(), like everything
   * else here — so Discard changes still gets you out of it.
   */
  resetToMachine(): void;
  /** Persist the pending changes. Resolves once the review has been re-read. */
  save(): Promise<void>;
  /** Throw the pending changes away and fall back to what is stored. */
  discard(): void;
}

const keyOf = (d: ReviewedDetectionDto) =>
  stableKeyFor(d.distance, d.mainCode, d.timeSeconds);

/**
 * A row counts towards the report ONLY while it is explicitly Approved.
 *
 * No status means not approved — including on lists stored before this rule, whose rows were
 * seeded from the machine output and carry `decision: "Accepted"` but no verdict. Nobody
 * approved those, so they do not show; Approve all is what turns them into results. Anything
 * moved OFF Approved (Draft / In review / Stale) likewise drops out of the graph and the
 * detection list, while staying in storage so the rejection is not lost and `approveAll()`
 * can bring it back.
 */
export const isApprovedRow = (d: ReviewedDetectionDto) => d.detectionStatus === "Approved";

/**
 * One side's approved detection list, and the only sanctioned way to change it.
 *
 * Both tabs use this so the OCR and analysis sides behave identically: same seeding rule, same
 * per-detection Status/Condition/Risk, same endpoint. Three invariants it exists to enforce:
 *
 * 1. **The machine output is never modified, and never shown as the report.** Every edit lands
 *    in the approved list; the graph and the detection list are built from approved rows alone.
 *    Until something is approved they are empty — the raw ocr.json / analysis output is still
 *    viewable through the source selector, but it is a machine proposal, not an inspection.
 * 2. **The first write copies the WHOLE machine list**, then applies the change. The approved
 *    list is therefore always a complete inspection with the reviewer's updates marked, rather
 *    than a lone row — which also keeps Compare honest, reporting untouched rows as unchanged
 *    instead of removed.
 * 3. **Nothing is written until the reviewer presses Save.** Edits accumulate in a local draft
 *    so a half-finished correction never reaches the server, and the page warns before an
 *    unload that would drop them.
 *
 * Saves are always side-scoped. A side-scoped save re-tags every row it carries, so the payload
 * must never include the other side's rows.
 */
const normalizeKey = (key: string) => key.trim().toLowerCase().replace(/\s+/g, " ");

/**
 * Whether every field in `header` is already stored with the same value.
 *
 * Only ever asked about the header the ANALYSIS side seeds, which is why a subset test is the
 * right one: the question is "does the server already hold what we would add", not "are these
 * two headers identical". A stored field the client does not carry is the reviewer's own and no
 * business of this comparison.
 */
function headerIsStored(
  header: readonly MainInformationDto[],
  stored: readonly MainInformationDto[]
): boolean {
  const byKey = new Map(stored.map((f) => [normalizeKey(f.key), (f.value ?? "").trim()]));
  return header.every((f) => byKey.get(normalizeKey(f.key)) === (f.value ?? "").trim());
}

export function useApprovedList({
  side,
  review,
  machineRows,
  mainInformation,
}: UseApprovedListArgs): ApprovedList {
  // Pending, unsaved reviewer changes. null = nothing pending, so what is stored is what shows.
  const [draft, setDraft] = useState<ReviewedDetectionDto[] | null>(null);

  /**
   * The same value, readable synchronously.
   *
   * save() used to read the `draft` its closure captured at render, so a caller that wrote rows
   * and then saved in the SAME tick -- upsert() ten times, then save() -- saw null and skipped
   * the write entirely. The rows appeared on screen as unsaved changes and never reached the
   * server. State drives rendering; this drives the save.
   */
  const draftRef = useRef<ReviewedDetectionDto[] | null>(null);

  const applyDraft = useCallback((next: ReviewedDetectionDto[] | null) => {
    draftRef.current = next;
    setDraft(next);
  }, []);

  // Memoised because the IDENTITY of these arrays matters, not just their contents: `rows`
  // feeds effects and memos on the page, and detectionsForSide() builds a new array on every
  // call. Unmemoised, every render produced a new list, which re-fired the page's mirror
  // effect, which wrote to the observable store, which re-rendered — "Maximum update depth
  // exceeded". `review.detections` is a frozen array, so its identity only changes when the
  // review is actually reloaded.
  const persisted = useMemo(
    () => review.detectionsForSide(side),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [review.detections, side]
  );

  // Opening another file (or the same one after a redo) must not carry the previous file's
  // pending edits over onto it.
  useEffect(() => {
    applyDraft(null);
  }, [review.fileId, side, applyDraft]);

  // Leaving with unsaved work would silently discard it — nothing has been written yet.
  useEffect(() => {
    if (draft === null) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [draft]);

  // This side's own sign-off. Asking the whole review would let the other process's rows answer
  // for this one. (Whether it CAN be signed off is read inside save(), after the write.)
  const isApprovedSide = review.isApprovedFor(side);

  const allRows = draft ?? persisted;
  const rows = useMemo(() => allRows.filter(isApprovedRow), [allRows]);

  // Every mutation starts from the pending draft, or — on the first change — from a full copy
  // of what is stored, falling back to the machine output so invariant 2 holds.
  const mutate = useCallback(
    (change: (base: ReviewedDetectionDto[]) => ReviewedDetectionDto[]) => {
      // Read through the ref, not a functional setState: consecutive calls in one tick each have
      // to see the previous one's result, and so does a save() that follows them.
      const current = draftRef.current;
      const base = (current ?? (persisted.length > 0 ? persisted : machineRows)).map((d) => ({
        ...d,
      }));
      applyDraft(change(base));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [persisted, machineRows, applyDraft]
  );

  const upsert = useCallback(
    (row: ReviewedDetectionDto, previous?: ReviewedDetectionDto) => {
      mutate((base) => {
        // Match on the row's ORIGINAL identity: editing distance or main code changes the key,
        // so matching on the new one would append a duplicate and orphan the row being edited.
        const targetKey = keyOf(previous ?? row);
        const index = base.findIndex((d) => keyOf(d) === targetKey);
        if (index >= 0) base[index] = { ...base[index], ...row };
        else base.push(row);
        return base;
      });
    },
    [mutate]
  );

  const remove = useCallback(
    (row: ReviewedDetectionDto) => {
      const target = keyOf(row);
      mutate((base) => base.filter((d) => keyOf(d) !== target));
    },
    [mutate]
  );

  const replace = useCallback(
    (next: ReviewedDetectionDto[]) => applyDraft(next),
    [applyDraft]
  );

  const approveAll = useCallback(() => {
    mutate((base) => {
      // Union with the machine output rather than just flipping what is already here: this is
      // also the way back for a row an earlier pass moved off Approved.
      const known = new Set(base.map(keyOf));
      const missing = machineRows.filter((d) => !known.has(keyOf(d)));
      return markAllApproved([...base, ...missing]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutate, machineRows]);

  const resetToMachine = useCallback(() => {
    // Copied, not referenced: machineRows is memoised upstream and handed to several consumers,
    // so mutating a row through the draft would reach back into the machine output the diff
    // markers are measured against.
    applyDraft(markAllApproved(machineRows.map((d) => ({ ...d }))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineRows, applyDraft]);

  /**
   * Whether the header this hook carries says something the server does not already hold.
   *
   * The analysis side seeds conventional fields the server has no other way to learn: a VA run
   * reads nothing off the video, so this hook is their only source. They used to be written only
   * alongside a detection draft, which meant a list that was ALREADY stored never carried them --
   * pressing Save skipped the write and went straight to the sign-off, so the header stayed as it
   * was and the ISYBAU kept failing on an empty Inspektionsrichtung while the reviewer pressed
   * the only button there was. Worse, once the side was approved the button greyed out entirely.
   *
   * So it decides two things: that a save has work to do, and that Save stays available to do it.
   *
   * Analysis only. The OCR header is read off the video and has its own route to the server;
   * writing it from here would put this hook in charge of data it does not own.
   */
  const headerNeedsWriting = useMemo(
    () =>
      side === "Analysis" &&
      mainInformation.length > 0 &&
      !headerIsStored(mainInformation, review.mainInformation ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [side, mainInformation, review.mainInformation]
  );

  /**
   * True from the press until the sign-off has come back.
   *
   * review.isSaving alone is not enough: it is raised and dropped by each request in turn, so it
   * read false in the gap between the list write and the approval -- long enough for the screen
   * to unblock and a second press to start another save over the first.
   */
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const save = useCallback(async () => {
    // A second press while the first is still running would write the same draft twice and
    // race two approvals.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await saveAndSignOff();
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, persisted, review, side, mainInformation, headerNeedsWriting]);

  const saveAndSignOff = async () => {
    const pending = draftRef.current;

    // Nothing pending is not the same as nothing to do: a list refused approval on an earlier
    // press is already stored, so this run has only the sign-off left. Returning here is what
    // deadlocked it — the button greyed out the moment the list was saved, and the approval it
    // still needed could never be attempted again.
    if (pending !== null || headerNeedsWriting) {
      const saved = await review.save({
        status: "InReview",
        comment: review.comment,
        side,
        // What is stored, when this is a header-only write. NOT an empty array: the server
        // replaces detections with whatever a save carries, so sending nothing here would
        // delete the very list the header was being added for.
        // Sorted by station so the list, the pipe diagram and the exports agree on order.
        detections: (pending ?? persisted)
          .map((d) => ({ ...d, side }))
          .sort((a, b) => Number(a.distance ?? 0) - Number(b.distance ?? 0)),
        // Omitted rather than sent empty. The server REPLACES main information with whatever a
        // save carries, so an empty list deletes the header -- and this is a detection-list save,
        // which knows nothing about the header. On a run without OCR that header is the only thing
        // standing between the reviewer and "at least one main information field must be filled
        // in": saving the list wiped it and the sign-off that followed was refused for its
        // absence. Absent means "not carrying one, leave it alone" (see ApplyMainInformation).
        mainInformation: mainInformation.length > 0 ? mainInformation : undefined,
      });

      // Keep the draft on failure — review.save() reports the reason in a snackbar and returns
      // false, and dropping the edits here would be the second thing to go wrong.
      if (saved === false) return;
      applyDraft(null);
    }

    // Saving the list and signing the review off are one gesture: the list IS what the
    // reviewer is approving, and the sign-off is what makes the server generate the standards
    // documents. There is no separate approve control, so without this the review would sit at
    // InReview forever and the ISYBAU/DWA exports would never appear.
    //
    // review.save() reloads before returning, so canApprove here is the server's verdict on
    // what was just written rather than on the previous state — it says no when nothing was
    // accepted, or when a run without OCR still has no main information. Approving is
    // therefore attempted only when it would succeed, and stays silent when it would not.
    // Read off the store NOW, not from this render's closure: a value captured at render predates
    // the save, so a list written for the first time still read "save the review first" and the
    // sign-off was skipped -- it took a second press, and a second full round of requests.
    const canApproveNow = side === "Analysis" ? review.analysisCanApprove : review.canApprove;
    if (canApproveNow) {
      // Scoped to this list: it generates this process's documents and leaves the other's alone.
      const approved = await review.approve(side);

      // approve() reloads before returning, so these flags say what the server actually HOLDS
      // now -- not what it was asked to write. A document is withheld when it cannot satisfy its
      // schema, and the analysis side is always built from the blank template (it has no source
      // document to copy), so a missing header field silently costs both files. Saying so here
      // is the difference between "the export is broken" and "fill in the two manholes".
      if (
        approved &&
        side === "Analysis" &&
        !review.videoAnalysisIsybauAvailable &&
        !review.videoAnalysisDwaAvailable
      ) {
        snackbarNotificationsService.showError("comparison.export.va-documents-missing");
      }

      return;
    }

    // Not approved -- and until now nothing said so. The list was stored and the frames were
    // stored, so from the outside the save looked complete while the two standards documents
    // simply never appeared. The server's reason names exactly what is missing, and it is
    // already a full sentence, so it is shown as one.
    const blockedReasonNow =
      side === "Analysis" ? review.analysisApprovalBlockedReason : review.approvalBlockedReason;
    if (side === "Analysis" && blockedReasonNow) {
      snackbarNotificationsService.showError(blockedReasonNow);
    }
  };

  const discard = useCallback(() => applyDraft(null), [applyDraft]);

  return {
    rows,
    allRows,
    notApprovedCount: allRows.length - rows.length,
    hasApproved: rows.length > 0,
    dirty: draft !== null,
    // Pending edits, or a stored list that still has to be signed off before its documents
    // can exist.
    // THIS side's sign-off: the other list being approved leaves this one's documents unwritten.
    // ...or a header this hook seeds that the server has not been told about yet, which an
    // already-approved side would otherwise have no way to send.
    canSubmit: draft !== null || headerNeedsWriting || (rows.length > 0 && !isApprovedSide),
    saving: submitting || review.isSaving,
    upsert,
    remove,
    replace,
    approveAll,
    resetToMachine,
    save,
    discard,
  };
}
